import { parseCalendarDate, formatCalendarDate as formatCalendarDateDisplay } from '../../../utils/appTimezone';
import { STOCK_LOCATIONS } from '../../../constants/siteCareModes';

const STOCK_STATUS_LABEL = {
  OK: 'En stock',
  LOW_STOCK: 'Stock bas',
  OUT_OF_STOCK: 'Rupture'
};

/** Libellés lieux de stock pour la liste ordonnance (prescripteurs). */
export const PRESCRIPTION_STOCK_LOCATION_LABEL = {
  [STOCK_LOCATIONS.MAIN]: 'Pharmacie',
  [STOCK_LOCATIONS.DUTY]: 'Armoire de garde'
};

const EXPIRY_WARNING_DAYS = 90;

export function normalizePrescriptionStockLocation(value) {
  const raw = String(value || '').toUpperCase().trim();
  if (raw === STOCK_LOCATIONS.DUTY) return STOCK_LOCATIONS.DUTY;
  return STOCK_LOCATIONS.MAIN;
}

export function stockEntryKey(medicationId, stockLocation = STOCK_LOCATIONS.MAIN) {
  return `${medicationId}:${normalizePrescriptionStockLocation(stockLocation)}`;
}

export function formatPrescriptionStockLocationLabel(stockLocation) {
  const loc = normalizePrescriptionStockLocation(stockLocation);
  return PRESCRIPTION_STOCK_LOCATION_LABEL[loc] || PRESCRIPTION_STOCK_LOCATION_LABEL[STOCK_LOCATIONS.MAIN];
}

/** Badge UI : « PHARMACIE » / « ARMOIRE DE GARDE ». */
export function formatPrescriptionStockLocationBadge(stockLocation) {
  return formatPrescriptionStockLocationLabel(stockLocation).toUpperCase();
}

/** « Amaday 10 (Pharmacie) » — utile pour libellés texte / recherche. */
export function formatMedicationCatalogName(baseName, stockLocation, { dualLocations = false } = {}) {
  const name = baseName != null ? String(baseName).trim() : '';
  if (!dualLocations || !name) return name;
  return `${name} (${formatPrescriptionStockLocationLabel(stockLocation)})`;
}

export function formatMedicationStockQty(onHand, unitName) {
  const qty = Number(onHand || 0);
  if (unitName) return `${qty} ${unitName}`;
  return String(qty);
}

export function formatMedicationPrice(price) {
  if (price == null || price === '' || Number.isNaN(Number(price))) return null;
  return `${Number(price).toLocaleString('fr-FR')} Ar`;
}

export function formatMedicationExpiry(value) {
  return formatCalendarDateDisplay(value) === '—' ? null : formatCalendarDateDisplay(value);
}

function getExpiryTone(expiryDate) {
  const d = parseCalendarDate(expiryDate);
  if (!d) return 'muted';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  if (d < today) return 'out';
  const limit = new Date(today);
  limit.setDate(limit.getDate() + EXPIRY_WARNING_DAYS);
  if (d <= limit) return 'low';
  return 'ok';
}

export function getMedicationAvailabilityMeta(stockRow, medFallback = null) {
  const unitName = stockRow?.unit_name || medFallback?.unit_name || '';
  const priceRaw = stockRow?.price ?? medFallback?.price ?? null;
  const priceLabel = formatMedicationPrice(priceRaw);
  const expiryRaw = stockRow?.nearest_expiry || null;
  const expiryFormatted = formatMedicationExpiry(expiryRaw);
  const expiryTone = getExpiryTone(expiryRaw);

  if (!stockRow) {
    return {
      qtyLabel: '—',
      priceLabel,
      expiryLabel: expiryFormatted ? `Pér. ${expiryFormatted}` : null,
      expiryTone,
      status: null,
      statusLabel: '',
      tone: 'unknown'
    };
  }

  const status = stockRow.stock_status || (Number(stockRow.on_hand || 0) > 0 ? 'OK' : 'OUT_OF_STOCK');
  const tone =
    status === 'OUT_OF_STOCK' ? 'out' : status === 'LOW_STOCK' ? 'low' : status === 'OK' ? 'ok' : 'unknown';

  return {
    qtyLabel: formatMedicationStockQty(stockRow.on_hand, unitName),
    priceLabel,
    expiryLabel: expiryFormatted ? `Pér. ${expiryFormatted}` : null,
    expiryTone,
    status,
    statusLabel: STOCK_STATUS_LABEL[status] || '',
    tone
  };
}

/** @deprecated use getMedicationAvailabilityMeta */
export function getMedicationStockMeta(stockRow, unitFallback = '') {
  const meta = getMedicationAvailabilityMeta(stockRow, { unit_name: unitFallback });
  return {
    qtyLabel: meta.qtyLabel,
    status: meta.status,
    statusLabel: meta.statusLabel,
    tone: meta.tone
  };
}

/**
 * Index stock : clé `medicationId:MAIN|DUTY` (+ alias medicationId si une seule ligne).
 */
export function stockMapFromItems(items = []) {
  const map = {};
  const counts = {};
  for (const row of items) {
    if (row?.medication_id == null) continue;
    const medId = String(row.medication_id);
    const key = stockEntryKey(medId, row.stock_location);
    map[key] = row;
    counts[medId] = (counts[medId] || 0) + 1;
  }
  for (const row of items) {
    if (row?.medication_id == null) continue;
    const medId = String(row.medication_id);
    if (counts[medId] === 1) {
      map[medId] = row;
    }
  }
  return map;
}

/** Fusion MAIN + DUTY, tri nom puis pharmacie avant armoire. */
export function mergeDualLocationStockRows(mainRows = [], dutyRows = []) {
  const taggedMain = (mainRows || []).map((row) => ({
    ...row,
    stock_location: STOCK_LOCATIONS.MAIN
  }));
  const taggedDuty = (dutyRows || []).map((row) => ({
    ...row,
    stock_location: STOCK_LOCATIONS.DUTY
  }));
  return [...taggedMain, ...taggedDuty].sort((a, b) => {
    const byName = String(a.medication_name || '').localeCompare(String(b.medication_name || ''), 'fr', {
      sensitivity: 'base'
    });
    if (byName !== 0) return byName;
    if (a.stock_location === b.stock_location) return 0;
    return a.stock_location === STOCK_LOCATIONS.MAIN ? -1 : 1;
  });
}

/**
 * Catalogue médicaments dérivé du stock site (même source que StockTable pharmacie).
 * Si `dualLocations`, chaque ligne est une entrée distincte (pharmacie / armoire).
 */
export function catalogFromPharmacyStockRows(rows = [], { dualLocations = false } = {}) {
  return rows
    .filter((row) => row.medication_status !== 'INACTIVE')
    .map((row) => {
      const stockLocation = normalizePrescriptionStockLocation(row.stock_location);
      const baseName = row.medication_name || '';
      return {
        id: row.medication_id,
        catalogKey: stockEntryKey(row.medication_id, stockLocation),
        stock_location: stockLocation,
        base_name: baseName,
        // Nom affiché dans le champ recherche = médicament seul ; le lieu est un badge.
        name: baseName,
        location_label: dualLocations ? formatPrescriptionStockLocationBadge(stockLocation) : null,
        unit_name: row.unit_name || '',
        price: row.price ?? null,
        status: row.medication_status || 'ACTIVE'
      };
    });
}

export function resolveMedicationStockRow(stockByMedicationId, med, stockReady) {
  if (!med?.id) return null;
  const key =
    med.catalogKey ||
    stockEntryKey(med.id, med.stock_location || STOCK_LOCATIONS.MAIN);
  const row = stockByMedicationId[key] || stockByMedicationId[String(med.id)];
  if (row) return row;
  if (!stockReady) return null;
  return {
    on_hand: 0,
    min_stock: 0,
    unit_name: med.unit_name || '',
    price: med.price ?? null,
    stock_status: 'OUT_OF_STOCK',
    nearest_expiry: null,
    stock_location: normalizePrescriptionStockLocation(med.stock_location)
  };
}

/**
 * Alerte à l'ajout en ordonnance — stock bas, rupture ou quantité > disponible.
 * @returns {null | { severity: 'low'|'out', title: string, message: string, detail: string, status: string }}
 */
export function getPrescriptionStockWarning(stockRow, { requestedQty = 1, med = null } = {}) {
  if (!stockRow) return null;

  const onHand = Number(stockRow.on_hand || 0);
  const minStock = Number(stockRow.min_stock || 0);
  const status =
    stockRow.stock_status || (onHand > 0 ? (onHand <= minStock ? 'LOW_STOCK' : 'OK') : 'OUT_OF_STOCK');
  const unitName = (stockRow.unit_name || med?.unit_name || '').trim();
  const qty = Number(requestedQty);
  const qtyLabel = Number.isFinite(qty) && qty > 0 ? qty : 1;
  const unitSuffix = unitName ? ` ${unitName}` : '';
  const location =
    stockRow.stock_location || med?.stock_location
      ? formatPrescriptionStockLocationLabel(stockRow.stock_location || med.stock_location)
      : null;
  const locationSuffix = location ? ` (${location})` : '';

  if (status === 'OUT_OF_STOCK' || onHand <= 0) {
    return {
      severity: 'out',
      title: 'Rupture de stock',
      message: `Ce médicament n’est pas disponible au stock${locationSuffix}.`,
      detail: `Disponible : 0${unitSuffix}`,
      status: 'OUT_OF_STOCK'
    };
  }

  const lowByThreshold = status === 'LOW_STOCK' || (minStock > 0 && onHand <= minStock);
  const insufficientQty = qtyLabel > onHand;

  if (insufficientQty) {
    return {
      severity: 'low',
      title: 'Stock insuffisant',
      message: `Quantité demandée (${qtyLabel}) supérieure au stock disponible (${onHand}${unitSuffix})${locationSuffix}.`,
      detail:
        minStock > 0
          ? `Disponible : ${onHand}${unitSuffix} · Seuil alerte : ${minStock}`
          : `Disponible : ${onHand}${unitSuffix}`,
      status: lowByThreshold ? 'LOW_STOCK' : status
    };
  }

  if (lowByThreshold) {
    return {
      severity: 'low',
      title: 'Stock faible',
      message: `Le stock de ce médicament est en dessous du seuil d’alerte${locationSuffix}.`,
      detail:
        minStock > 0
          ? `Disponible : ${onHand}${unitSuffix} · Seuil : ${minStock}`
          : `Disponible : ${onHand}${unitSuffix}`,
      status: 'LOW_STOCK'
    };
  }

  return null;
}
